import { getBomProductPicks, type BomProductPick } from '@api/bom-management';
import {
    cancelWorkOrder,
    deleteWorkOrderAttachment,
    getPeggedPosForWorkOrder,
    getWorkOrder,
    listVendorsForPicker,
    listWarehousesForPicker,
    listWorkOrderAttachments,
    updateWorkOrderStatus,
    uploadWorkOrderAttachment,
    type PeggedPoSummary,
    type VendorPickItem,
    type WarehousePickItem,
    type WorkOrderAttachment,
    type WorkOrderMaterial,
    type WorkOrderStatus,
} from '@api/work-order';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import useToaster from '@hooks/toaster/useToaster';
import { ActionIcon, Button, FileButton, Group, Loader, Paper, Select, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
    ArrowLeftIcon,
    DownloadSimpleIcon,
    PaperclipIcon,
    PlusIcon,
    TrashIcon,
    XIcon,
} from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router';

import StatusPill from './StatusPill';
import { formatCount } from '@utils/currency';

const STATUS_LABEL: Record<string, string> = {
    DRAFT: 'Draft',
    ISSUED: 'Issued',
    IN_PRODUCTION: 'In Production',
    READY_TO_SHIP: 'Ready to Ship',
    SHIPPED: 'Shipped',
    RECEIVED: 'Received',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
};

const STATUS_DROPDOWN_OPTIONS: Array<{ value: WorkOrderStatus; label: string }> = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ISSUED', label: 'Issued' },
    { value: 'IN_PRODUCTION', label: 'In Production' },
    { value: 'READY_TO_SHIP', label: 'Ready to Ship' },
    { value: 'SHIPPED', label: 'Shipped' },
    { value: 'RECEIVED', label: 'Received' },
];

const TERMINAL_STATUSES: WorkOrderStatus[] = ['RECEIVED', 'CANCELLED', 'COMPLETED'];

const WorkOrderDetailContainer: FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { showToastSuccess, showToastError } = useToaster();
    const queryClient = useQueryClient();
    const [statusActionInFlight, setStatusActionInFlight] = useState(false);

    const handleRaisePo = (material: WorkOrderMaterial, code: string) => {
        const params = new URLSearchParams({
            workOrderId: id ?? '',
            workOrderCode: code,
            materialId: material.materialId,
            materialName: material.materialName,
            qty: String(material.deficitQty),
        });
        if (material.unit) params.set('unit', material.unit);
        // Raised on Orders like any other purchase order; Orders reads the hand-off and pegs it.
        void navigate(`/orders?${params.toString()}`);
    };

    const workOrderQuery = useQuery({
        queryKey: ['work-order', id],
        queryFn: () => getWorkOrder(id ?? ''),
        enabled: !!id,
    });

    const peggedPosQuery = useQuery({
        queryKey: ['work-order-pegged-pos', id],
        queryFn: () => getPeggedPosForWorkOrder(id ?? ''),
        enabled: !!id,
    });

    const attachmentsQuery = useQuery({
        queryKey: ['work-order-attachments', id],
        queryFn: () => listWorkOrderAttachments(id ?? ''),
        enabled: !!id,
    });

    const [attachmentActionInFlight, setAttachmentActionInFlight] = useState(false);

    const handleUploadAttachments = async (files: File[] | null) => {
        if (!id || !files || files.length === 0) return;
        setAttachmentActionInFlight(true);
        const failures: string[] = [];
        for (const file of files) {
            try {
                await uploadWorkOrderAttachment(id, file);
            } catch {
                failures.push(file.name);
            }
        }
        await queryClient.invalidateQueries({ queryKey: ['work-order-attachments', id] });
        if (failures.length > 0) {
            showToastError({
                title: 'Some uploads failed',
                subtitle: failures.join(', '),
            });
        } else {
            showToastSuccess({
                title: `Uploaded ${files.length} file${files.length === 1 ? '' : 's'}`,
            });
        }
        setAttachmentActionInFlight(false);
    };

    const handleDeleteAttachment = (attachment: WorkOrderAttachment) => {
        if (!id) return;
        modals.openConfirmModal({
            title: `Delete ${attachment.fileName}?`,
            children: (
                <Text size="sm">
                    This will remove the attachment from this work order. The file
                    cannot be recovered.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Keep' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setAttachmentActionInFlight(true);
                try {
                    await deleteWorkOrderAttachment(id, attachment.id);
                    await queryClient.invalidateQueries({
                        queryKey: ['work-order-attachments', id],
                    });
                    showToastSuccess({ title: `Removed ${attachment.fileName}` });
                } catch (err: any) {
                    const message = err?.error?.message ?? err?.message ?? 'Failed to delete';
                    showToastError({ title: 'Failed to delete', subtitle: message });
                } finally {
                    setAttachmentActionInFlight(false);
                }
            },
        });
    };

    const invalidateWorkOrderData = () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: ['work-order', id] }),
            queryClient.invalidateQueries({ queryKey: ['work-order-pegged-pos', id] }),
            queryClient.invalidateQueries({ queryKey: ['work-orders'] }),
        ]);

    const performStatusUpdate = async (newStatus: WorkOrderStatus, code: string) => {
        if (!id) return;
        setStatusActionInFlight(true);
        try {
            const updated = await updateWorkOrderStatus(id, newStatus);
            await invalidateWorkOrderData();
            showToastSuccess({
                title: `${updated.code} marked ${STATUS_LABEL[newStatus] ?? newStatus}`,
            });
        } catch (err: any) {
            const message = err?.error?.message ?? err?.message ?? 'Failed to update status';
            showToastError({ title: `Failed to update ${code}`, subtitle: message });
        } finally {
            setStatusActionInFlight(false);
        }
    };

    const handleStatusChange = (newStatus: WorkOrderStatus | null) => {
        if (!newStatus || !workOrderQuery.data) return;
        if (newStatus === workOrderQuery.data.status) return;
        const code = workOrderQuery.data.code;

        // Confirm the irreversible terminal transition.
        if (newStatus === 'RECEIVED') {
            modals.openConfirmModal({
                title: `Mark ${code} as Received?`,
                children: (
                    <Text size="sm">
                        Received is a terminal state — no further status changes are
                        allowed after this.
                    </Text>
                ),
                labels: { confirm: 'Mark Received', cancel: 'Back' },
                onConfirm: () => performStatusUpdate(newStatus, code),
            });
            return;
        }

        void performStatusUpdate(newStatus, code);
    };

    const handleCancel = () => {
        if (!id || !workOrderQuery.data) return;
        const code = workOrderQuery.data.code;
        modals.openConfirmModal({
            title: `Cancel ${code}?`,
            children: (
                <Text size="sm">
                    Allocations will be cleared. Any POs already raised for this work
                    order stay in the system — they'll continue through their normal
                    lifecycle and arrive as un-pegged free supply.
                </Text>
            ),
            labels: { confirm: 'Cancel work order', cancel: 'Keep' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                if (!id) return;
                setStatusActionInFlight(true);
                try {
                    await cancelWorkOrder(id);
                    await invalidateWorkOrderData();
                    showToastSuccess({ title: `Work order ${code} cancelled` });
                } catch (err: any) {
                    const message = err?.error?.message ?? err?.message ?? 'Failed to cancel';
                    showToastError({ title: 'Failed to cancel', subtitle: message });
                } finally {
                    setStatusActionInFlight(false);
                }
            },
        });
    };

    const productsQuery = useQuery<BomProductPick[]>({
        queryKey: ['bom-product-picks'],
        queryFn: getBomProductPicks,
    });

    const warehousesQuery = useQuery<WarehousePickItem[]>({
        queryKey: ['wo-warehouses'],
        queryFn: listWarehousesForPicker,
    });

    const vendorsQuery = useQuery<VendorPickItem[]>({
        queryKey: ['wo-vendors'],
        queryFn: listVendorsForPicker,
    });

    const wo = workOrderQuery.data;

    const productName = useMemo(() => {
        if (!wo) return '';
        const match = (productsQuery.data ?? []).find((p) => p.productId === wo.productId);
        return match?.name ?? wo.productId;
    }, [wo, productsQuery.data]);

    const locationName = useMemo(() => {
        if (!wo) return '';
        if (wo.productionLocationType === 'WAREHOUSE') {
            const match = (warehousesQuery.data ?? []).find(
                (w) => w.warehouseId === wo.productionLocationId,
            );
            return match?.name ?? wo.productionLocationId;
        }
        const match = (vendorsQuery.data ?? []).find(
            (v) => v.vendorId === wo.productionLocationId,
        );
        return match?.name ?? wo.productionLocationId;
    }, [wo, warehousesQuery.data, vendorsQuery.data]);

    const materialColumns: DataSheetColumn<WorkOrderMaterial>[] = [
        { key: 'materialName', header: 'Material', width: 240, sticky: true },
        { key: 'unit', header: 'Unit', width: 80 },
        {
            key: 'requiredQty',
            header: 'Required',
            width: 110,
            align: 'right',
            render: (value) => formatCount(value),
        },
        {
            key: 'allocatedQty',
            header: 'Allocated',
            width: 110,
            align: 'right',
            render: (value) => formatCount(value),
        },
        {
            key: 'deficitQty',
            header: 'Deficit',
            width: 110,
            align: 'right',
            render: (value) => {
                const n = Number(value);
                return n > 0 ? (
                    <Text c="red" fw={600} size="sm">
                        {formatCount(n)}
                    </Text>
                ) : (
                    <Text c="dimmed" size="sm">—</Text>
                );
            },
        },
        {
            key: 'allocations',
            header: 'Sources',
            width: 360,
            render: (_, row) => {
                if (!row.allocations.length) {
                    return <Text c="dimmed" size="sm">No coverage yet</Text>;
                }
                return (
                    <Stack gap={4}>
                        {row.allocations.map((a, idx) => (
                            <Text key={`${a.sourceType}-${a.sourceId}-${idx}`} size="sm">
                                {sourceTypeLabel(a.sourceType)} · {a.sourceName} · {formatCount(a.allocatedQty)}
                            </Text>
                        ))}
                    </Stack>
                );
            },
        },
        {
            key: 'raisePo',
            header: '',
            width: 120,
            render: (_, row) => {
                if (row.deficitQty <= 0) return null;
                return (
                    <Button
                        size="xs"
                        variant={ButtonVariant.SUBTLE_ACCENTED}
                        leftSection={<PlusIcon size={12} />}
                        onClick={() => wo && handleRaisePo(row, wo.code)}
                    >
                        Raise PO
                    </Button>
                );
            },
        },
    ];

    const peggedPoColumns: DataSheetColumn<PeggedPoSummary>[] = [
        { key: 'purchaseOrderId', header: 'PO', width: 200, sticky: true },
        { key: 'status', header: 'Status', width: 160 },
        {
            key: 'vendorName',
            header: 'Vendor',
            width: 240,
            render: (value) => value ? String(value) : <Text c="dimmed" size="sm">—</Text>,
        },
        {
            key: 'peggedQty',
            header: 'Pegged qty',
            width: 130,
            align: 'right',
            render: (value) => formatCount(value),
        },
        {
            key: 'poDate',
            header: 'PO date',
            width: 110,
            render: (value) => value ? String(value) : <Text c="dimmed" size="sm">—</Text>,
        },
    ];

    if (workOrderQuery.isLoading) {
        return (
            <div className="flex w-full flex-col gap-6 p-8">
                <Group justify="center" p="xl">
                    <Loader />
                </Group>
            </div>
        );
    }

    if (workOrderQuery.isError || !wo) {
        return (
            <div className="flex w-full flex-col gap-6 p-8">
                <Button
                    variant={ButtonVariant.DEFAULT}
                    leftSection={<ArrowLeftIcon size={16} />}
                    onClick={() => navigate('/work-orders')}
                    w="fit-content"
                >
                    Back to work orders
                </Button>
                <Text c="red">Could not load this work order.</Text>
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col gap-6 p-8">
            <Group justify="space-between" align="flex-start">
                <PageHeader
                    icon="🏭"
                    title={wo.code}
                    subtitle={`${productName} · ${formatCount(wo.quantity)} units due ${wo.dueDate}`}
                />
                <Group gap="sm">
                    {TERMINAL_STATUSES.includes(wo.status) ? null : (
                        <>
                            <Select
                                data={STATUS_DROPDOWN_OPTIONS}
                                value={wo.status}
                                onChange={(value) => handleStatusChange(value as WorkOrderStatus | null)}
                                disabled={statusActionInFlight}
                                allowDeselect={false}
                                w={180}
                                aria-label="Change status"
                            />
                            <Button
                                variant={ButtonVariant.OUTLINED_DESTRUCTIVE}
                                leftSection={<XIcon size={16} />}
                                onClick={handleCancel}
                                disabled={statusActionInFlight}
                            >
                                Cancel WO
                            </Button>
                        </>
                    )}
                    <Button
                        variant={ButtonVariant.DEFAULT}
                        leftSection={<ArrowLeftIcon size={16} />}
                        onClick={() => navigate('/work-orders')}
                    >
                        Back to list
                    </Button>
                </Group>
            </Group>

            <Paper withBorder p="md" mb="md">
                <Group gap="xl">
                    <SummaryField label="Status">
                        <StatusPill status={wo.status} />
                    </SummaryField>
                    <SummaryField label="Production location">
                        <Text size="sm">
                            {locationName}
                            {wo.productionLocationType === 'VENDOR' ? ' (vendor)' : ''}
                        </Text>
                    </SummaryField>
                    <SummaryField label="Due date">
                        <Text size="sm">{wo.dueDate}</Text>
                    </SummaryField>
                    <SummaryField label="Created">
                        <Text size="sm">
                            {wo.createdAt ? new Date(wo.createdAt).toISOString().slice(0, 10) : '—'}
                        </Text>
                    </SummaryField>
                </Group>
                {wo.notes ? (
                    <Text size="sm" mt="sm" c="dimmed">
                        {wo.notes}
                    </Text>
                ) : null}
            </Paper>

            <DataSheet<WorkOrderMaterial>
                data={wo.materials ?? []}
                columns={materialColumns}
                title="Materials"
                subtitle={`${(wo.materials ?? []).length} required · ${(wo.materials ?? []).filter((m) => m.deficitQty > 0).length} in deficit`}
                getRowKey={(row) => row.materialId}
                maxHeight="auto"
                emptyState={<Text p="md" c="dimmed">This work order has no material requirements.</Text>}
            />

            <DataSheet<PeggedPoSummary>
                data={peggedPosQuery.data ?? []}
                columns={peggedPoColumns}
                title="Purchase orders raised for this WO"
                subtitle={
                    peggedPosQuery.data?.length
                        ? `${peggedPosQuery.data.length} pegged`
                        : 'None yet'
                }
                isLoading={peggedPosQuery.isLoading}
                getRowKey={(row) => row.purchaseOrderId}
                onRowClick={(row) => void navigate(`/purchase-orders?poId=${encodeURIComponent(row.purchaseOrderId)}`)}
                maxHeight="auto"
                emptyState={
                    <Text p="md" c="dimmed">
                        No POs have been raised for this work order yet. Click "Raise PO"
                        on any deficit row above.
                    </Text>
                }
            />

            <Paper withBorder p="md">
                <Group justify="space-between" align="center" mb="sm">
                    <Stack gap={4}>
                        <Text fw={600} size="sm">Attachments</Text>
                        <Text size="xs" c="dimmed">
                            {attachmentsQuery.data?.length
                                ? `${attachmentsQuery.data.length} file${attachmentsQuery.data.length === 1 ? '' : 's'}`
                                : 'No attachments yet'}
                        </Text>
                    </Stack>
                    <FileButton onChange={handleUploadAttachments} multiple>
                        {(props) => (
                            <Button
                                {...props}
                                variant={ButtonVariant.DEFAULT}
                                leftSection={<PaperclipIcon size={14} />}
                                size="xs"
                                loading={attachmentActionInFlight}
                            >
                                Add files
                            </Button>
                        )}
                    </FileButton>
                </Group>

                {attachmentsQuery.isLoading ? (
                    <Loader size="sm" />
                ) : !attachmentsQuery.data?.length ? (
                    <Text size="sm" c="dimmed">
                        Upload production specs, quality checklists, or supplier instructions
                        to keep them tied to this work order.
                    </Text>
                ) : (
                    <Stack gap={8}>
                        {attachmentsQuery.data.map((att) => (
                            <Group key={att.id} justify="space-between" gap="sm">
                                <Stack gap={4}>
                                    <Text size="sm">{att.fileName}</Text>
                                    <Text size="xs" c="dimmed">
                                        {formatFileSize(att.fileSize)} · {att.contentType} · {new Date(att.createdAt).toISOString().slice(0, 10)}
                                    </Text>
                                </Stack>
                                <Group gap={4}>
                                    {att.downloadUrl ? (
                                        <ActionIcon
                                            component="a"
                                            href={att.downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant={ButtonVariant.SUBTLE}
                                            aria-label={`Download ${att.fileName}`}
                                        >
                                            <DownloadSimpleIcon size={16} />
                                        </ActionIcon>
                                    ) : null}
                                    <ActionIcon
                                        variant={ButtonVariant.SUBTLE}
                                        color="red"
                                        onClick={() => handleDeleteAttachment(att)}
                                        disabled={attachmentActionInFlight}
                                        aria-label={`Delete ${att.fileName}`}
                                    >
                                        <TrashIcon size={16} />
                                    </ActionIcon>
                                </Group>
                            </Group>
                        ))}
                    </Stack>
                )}
            </Paper>
        </div>
    );
};

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SummaryField: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Stack gap={4}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
        {children}
    </Stack>
);

const sourceTypeLabel = (
    sourceType: WorkOrderMaterial['allocations'][number]['sourceType'],
): string => {
    switch (sourceType) {
        case 'WAREHOUSE':
            return 'Warehouse';
        case 'VENDOR':
            return 'Vendor';
        case 'INCOMING_PO':
            return 'Incoming PO';
        default:
            return sourceType;
    }
};

export default WorkOrderDetailContainer;
